/**
 * Fire-and-forget reporting of CLIENT-SIDE reCAPTCHA mint failures.
 *
 * A client-side failure (script blocked, anchor-frame stall / timeout,
 * empty token, exec-callback rejection) means no token was ever minted, so
 * the request never reaches Cognito and the server-side DefineAuthChallenge
 * gate never runs — the failure is otherwise invisible to us (the web app
 * ships no RUM/analytics). This module POSTs a small, PII-free report to the
 * infra repo's `/auth/client-event` ingest so those failures land in the
 * same CloudWatch failure log as server-side rejections and are queryable
 * the same way.
 *
 * Transport: `navigator.sendBeacon` with a `text/plain` body. text/plain is
 * a CORS-safelisted content type, so the cross-origin POST is a "simple"
 * request with no preflight (which sendBeacon can't perform) — the server
 * JSON-parses the text. sendBeacon is also queued by the browser and
 * survives the page navigation that a successful auth flow triggers.
 *
 * Best-effort by contract: never throws, swallows every error, and no-ops
 * when the endpoint isn't configured. It must not perturb the auth flow it
 * is reporting on.
 */

import { authConfig } from '../config/auth-config.ts';
import { stage } from '../config/stage.ts';
import { isRecaptchaError } from './recaptcha.ts';
import { requestTimeout } from '../config/requestTimeout.ts';

/** Which auth flow the failure occurred in. */
export type RecaptchaFlow = 'signin' | 'signup';

/** Wire shape of a client-side reCAPTCHA failure report. PII-free. */
interface RecaptchaFailureReport {
  readonly kind: 'recaptcha_client_failure';
  readonly stage: string;
  readonly flow: RecaptchaFlow;
  /** Structured cause; `unknown` when the thrown error wasn't a RecaptchaError. */
  readonly reason: string;
  readonly message: string;
  /** Extra non-PII detail (e.g. grecaptcha's reject argument), when present. */
  readonly detail?: string;
  /** `navigator.onLine` at failure time — distinguishes offline from a stall. */
  readonly online?: boolean;
  /** How long the mint ran before failing (ms); ~mint duration for a timeout. */
  readonly elapsedMs?: number;
}

/**
 * Report a client-side reCAPTCHA mint failure. Safe to call from any auth
 * catch block; only actual reCAPTCHA failures carry useful `reason`/`detail`.
 *
 * @param err       the thrown error (ideally a `RecaptchaError`)
 * @param flow      which auth flow ('signin' | 'signup')
 * @param elapsedMs optional wall-clock ms spent before the failure
 */
export function reportRecaptchaFailure(
  err: unknown,
  flow: RecaptchaFlow,
  elapsedMs?: number,
): void {
  try {
    const url = authConfig.clientEventUrl;
    if (!url) {
      return;
    }

    const report: RecaptchaFailureReport = {
      kind: 'recaptcha_client_failure',
      stage,
      flow,
      reason: isRecaptchaError(err) ? err.reason : 'unknown',
      message: err instanceof Error ? err.message : String(err),
      detail: isRecaptchaError(err) ? err.detail : undefined,
      online:
        typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      elapsedMs,
    };
    const body = JSON.stringify(report);

    // sendBeacon path (primary). text/plain keeps this a CORS-simple request.
    if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      navigator.sendBeacon(url, blob);
      return;
    }

    // Fallback for the rare environment without sendBeacon. keepalive lets
    // it outlive the imminent navigation; text/plain again avoids preflight.
    if (typeof fetch === 'function') {
      void fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'text/plain;charset=UTF-8' },
        body,
        keepalive: true,
        signal: requestTimeout(),
      }).catch(() => {
        /* best-effort telemetry — ignore */
      });
    }
  } catch {
    /* telemetry must never perturb the auth flow */
  }
}
